import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Button, Divider, Form, Input, InputNumber, List, Modal, Space, Tag, Typography,
} from 'antd';
import { CopyOutlined, FileImageOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';
import { getDisplayNumber } from '../utils/productNumbering';
import {
  STANDARD_COPY_FIELD_BEHAVIOR, CUSTOM_COPY_FIELD_BEHAVIOR, buildProductCopyDraft, buildCustomCopyDraft,
} from '../utils/productCopy';

const { Search } = Input;
const { Text } = Typography;

const BEHAVIOR_TONE = {
  copied: { color: 'default', label: 'Copied automatically' },
  suffixed: { color: 'default', label: 'Copied automatically' },
  linked: { color: 'blue', label: 'Copied automatically' },
  regenerated: { color: 'blue', label: 'Copied automatically' },
  cleared: { color: 'orange', label: 'Cleared — please complete' },
  reset: { color: 'orange', label: 'Cleared — please complete' },
  'not-copied': { color: 'red', label: 'What will not be copied' },
  inherited: { color: 'gold', label: 'Inherited — verification required' },
};

export default function CopyProductModal({
  open,
  onCancel,
  onConfirm,
  products = [],
  suppliers = [],
  categories = [],
  initialSourceProduct = null,
  // Set when opened from a Category Details Selection Wizard's Products
  // step ("Copy Existing"): always produces a Custom product regardless of
  // the source's own type, locks Category to the item's own category, and
  // collects a price for this job (a normal copy stays price-less — prices
  // are never copied between customers).
  forceProductType = null,
  lockedCategoryId = null,
  requirePrice = false,
}) {
  const { t } = useI18n();
  const [step, setStep] = useState('select');
  const [searchText, setSearchText] = useState('');
  const [sourceProduct, setSourceProduct] = useState(null);
  const [form] = Form.useForm();

  const effectiveType = forceProductType || sourceProduct?.productType;

  useEffect(() => {
    if (!open) return;
    if (initialSourceProduct) {
      setSourceProduct(initialSourceProduct);
      setStep('review');
      form.setFieldsValue({
        name: `${initialSourceProduct.name} (copy)`,
        supplierItemNumber: '',
        colour: '',
        avgItemPrice: null,
      });
    } else {
      setSourceProduct(null);
      setStep('select');
    }
    setSearchText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSourceProduct]);

  const filteredProducts = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return products.filter((product) => {
      if (product.status === 'Inactive') return false;
      if (!query) return true;
      const number = getDisplayNumber(product) || '';
      const supplier = suppliers.find((s) => s.id === product.supplierId)?.name || '';
      return (
        product.name.toLowerCase().includes(query)
        || number.toLowerCase().includes(query)
        || supplier.toLowerCase().includes(query)
      );
    });
  }, [products, searchText, suppliers]);

  const draft = useMemo(() => {
    if (!sourceProduct) return null;
    if (forceProductType === 'Custom' && sourceProduct.productType !== 'Custom') {
      const customDraft = buildCustomCopyDraft(sourceProduct, products);
      return lockedCategoryId != null ? { ...customDraft, categoryId: lockedCategoryId } : customDraft;
    }
    const copyDraft = buildProductCopyDraft(sourceProduct, products);
    return lockedCategoryId != null ? { ...copyDraft, categoryId: lockedCategoryId } : copyDraft;
  }, [sourceProduct, products, forceProductType, lockedCategoryId]);

  const behaviorRows = effectiveType === 'Custom'
    ? CUSTOM_COPY_FIELD_BEHAVIOR
    : STANDARD_COPY_FIELD_BEHAVIOR;

  const handleSelectSource = (product) => {
    setSourceProduct(product);
    setStep('review');
    setTimeout(() => {
      form.setFieldsValue({
        name: `${product.name} (copy)`,
        supplierItemNumber: '',
        colour: '',
        avgItemPrice: null,
      });
    }, 0);
  };

  const handleBack = () => {
    setStep('select');
    setSourceProduct(null);
  };

  const handleClose = () => {
    setStep('select');
    setSourceProduct(null);
    setSearchText('');
    onCancel();
  };

  const handleConfirm = async () => {
    const values = await form.validateFields();
    onConfirm({ ...draft, ...values, copiedFromProductId: sourceProduct.id }, sourceProduct);
    handleClose();
  };

  return (
    <Modal
      title={step === 'select' ? t('Select Product to Copy') : t('Review Copy')}
      open={open}
      onCancel={handleClose}
      width={640}
      footer={step === 'select' ? null : [
        <Button key="back" onClick={handleBack}>{t('Back')}</Button>,
        <Button key="submit" type="primary" icon={<CopyOutlined />} onClick={handleConfirm}>
          {t('Create Copy')}
        </Button>,
      ]}
      destroyOnClose
    >
      {step === 'select' ? (
        <>
          <Search
            placeholder={t('Search by product number, name, or supplier')}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            style={{ marginBottom: 12 }}
            allowClear
          />
          <List
            dataSource={filteredProducts}
            style={{ maxHeight: 420, overflowY: 'auto' }}
            renderItem={(product) => {
              const supplier = suppliers.find((s) => s.id === product.supplierId);
              const category = categories.find((c) => c.id === product.categoryId);
              const primaryImage = product.primaryImageId ? undefined : undefined;
              return (
                <List.Item
                  key={product.id}
                  onClick={() => handleSelectSource(product)}
                  style={{ cursor: 'pointer', padding: '10px 8px' }}
                  className="copy-product-list-item"
                >
                  <List.Item.Meta
                    avatar={<Avatar shape="square" size={40} icon={<FileImageOutlined />} src={primaryImage} />}
                    title={(
                      <Space size={8}>
                        <span>{product.name}</span>
                        <Tag color={product.productType === 'Custom' ? 'purple' : 'blue'}>
                          {t(product.productType === 'Custom' ? 'Custom Product' : 'Standard Product')}
                        </Tag>
                      </Space>
                    )}
                    description={(
                      <span>
                        {getDisplayNumber(product)} · {supplier?.name || '—'} · {category?.name || t('Uncategorized')}
                      </span>
                    )}
                  />
                </List.Item>
              );
            }}
          />
        </>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            message={t('Copying from {{name}}', { name: sourceProduct.name })}
            style={{ marginBottom: 16 }}
          />
          <Form form={form} layout="vertical">
            <Form.Item
              label={t('Product Name')}
              name="name"
              rules={[{ required: true, message: t('Please enter product name') }]}
            >
              <Input />
            </Form.Item>
            {effectiveType === 'Standard' && (
              <>
                <Form.Item label={t('Supplier Item Number')} name="supplierItemNumber">
                  <Input placeholder={t('What you need to change')} autoFocus />
                </Form.Item>
                <Form.Item label={t('Colour')} name="colour">
                  <Input placeholder={t('What you need to change')} />
                </Form.Item>
              </>
            )}
            {requirePrice && (
              <Form.Item
                label={t('Average Item Price')}
                name="avgItemPrice"
                extra={t('The price for this job — prices are never copied from the source product.')}
                rules={[{ required: true, message: t('Please enter a price') }]}
              >
                <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} placeholder={t('Not provided')} addonAfter="DKK" />
              </Form.Item>
            )}
          </Form>
          <Divider plain>{t('What will be copied')}</Divider>
          <List
            size="small"
            dataSource={behaviorRows}
            renderItem={(row) => {
              const tone = BEHAVIOR_TONE[row.behavior] || BEHAVIOR_TONE.copied;
              return (
                <List.Item key={row.field} style={{ padding: '6px 0' }}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text>{t(row.label)}</Text>
                    <Tag color={tone.color}>{t(tone.label)}</Tag>
                  </Space>
                </List.Item>
              );
            }}
          />
          {effectiveType === 'Custom' && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message={t('Prices are not copied between customers.')}
            />
          )}
        </>
      )}
    </Modal>
  );
}
